import { ReactElement } from "react";

// lead query parameters types
export interface LeadQueryParams {
  src: string | null;
  type: string | null;
  camp: string | null;
  campaign_id?: string | null;
  comingFrom?: string | null;
  campaign_name?: string | null;
  gcl_id?: string | null;
  camp_id: string | null;
}

export interface LoaderProps {
  backgroundColor?: string;
  textColor?: string;
  fontSize?: string;
  borderColor?: string;
}

export interface attendWebinarType {
  eligible: boolean;
  show: boolean;
  value: null | string;
  data: any[];
  loader: boolean;
  heading?: string;
}

export type AlertInputs = {
  type: string | "success" | "warning" | "error" | "info";
  text?: string;
  html?: string;
};

export type Categories = {
  id: string;
  name: string;
  slug: string;
  file_link: string;
  created_at: string;
  updated_at: string;
  vertical: string;
};

export type Tags = {
  id: string;
  pages: Pages[];
  name: string;
  slug: string;
  created_at: string;
  updated_at: string;
  categories: string;
};

export type Pages = {
  name: string;
  slug: string;
};

export type PageDetails = {
  abstract: string;
  active: boolean;
  content: string;
  created_at: string;
  id: string;
  name: string;
  pinned_page: boolean;
  placeholder: string;
  published_on: string;
  subtitle: string;
  tags: string;
  title: string;
  updated: boolean;
  updated_at: string;
  video: string;
};

export type PinnedPage = {
  name: string;
  slug: string;
  tag: string;
  tagSlug: string;
  created_at: string;
  updated_at: string;
};

export type CmsBannerDetails = {
  id: string;
  metadata: CmsMetaData[];
  page_banner: string;
  title: string;
  subtitle: string;
  subtitle2: string;
  cta_text1: string;
  cta_link: string;
  cta_text2: string;
  cta_link2: string;
  banner_CTA_text: string;
  banner_CTA_link: string;
  side_banner: string;
  side_banner_link: string;
  side_video: string;
  side_title: string;
  side_content: string;
};

export type CmsMetaData = {
  id: string;
  description: string;
  keywords: string;
  canonical_tags: string;
  og_title: string;
  og_description: string;
  og_site_name: string;
  og_url: string;
  og_type: string;
  og_locale: string;
  twitter_card: string;
  twitter_title: string;
  twitter_description: string;
  og_image: string;
  og_image_url: string;
  twitter_image: string;
  twitter_image_url: string;
  page: string;
  homepage: string;
};

export type ReasonProps = {
  children?: ReactElement;
  reasonKey: string;
  reasonIcon: string;
  reasonTitle: string;
  reasonSubtitle: string;
  reasonSubtitleWidth: string;
};

export type PositionWithIIMsCategoryProps = {
  categoryItem1: string;
  categoryItem2: string;
  categoryItem3: string;
};

export type CategoryBtnProps = {
  btnKey: string;
  btnTitle: string;
};

export type Alumni = {
  name: string;
  img: string;
  alt?: string;
  description: string;
  des: string;
  compLogo: string;
  alliance: string;
  location: string;
  linkdin: string;
  storyYoutubeVideoLink: string;
};

export type FAQQuestions = {
  id: number;
  question: string;
  answer: string;
};

export type FAQType = {
  id: number;
  title: string;
  questions: FAQQuestions[];
};

export type BlogPost = {
  id: string;
  author: Author;
  category: BlogCategory;
  sub_category: string | null;
  tags: BlogTags[];
  metadata: BlogMetaData[];
  title: string;
  subtitle: string;
  blog_abstract: string;
  blog_content: string;
  slug: string;
  schema: string;
  likes: number;
  comments: number;
  views: number;
  add_to_app: boolean;
  read_time: string;
  publish_date: string;
  modified_date: string | null;
  editor: string | null;
  banner_url: string;
  featured: boolean;
  canonical_url: string;
  created_at: string;
  updated_at: string;
  active: boolean;
  trending: boolean;
  mobile_banner: string;
  mobile_banner_url: string;
  created_by: string;
  updated_by: string | null;
};

export type Author = {
  id: string;
  name: string;
  specializations: string;
  image_url: string;
  title: string;
  description: string;
  url: string;
  facebook: string | null;
  twitter: string | null;
  linkedin: string;
  instagram: string | null;
  created_at: string;
  updated_at: string;
  active: boolean;
  created_by: string;
  updated_by: string | null;
};

export type BlogCategory = {
  id: string;
  category: string;
  category_cleaned: string;
  images: string | null;
};

export type BlogTags = {
  tags: string;
  images: string;
};

export type BlogMetaData = {
  id: string;
  description: string;
  keywords: string | null;
  canonical_tags: string | null;
  og_title: string;
  og_description: string;
  og_site_name: string;
  og_url: string;
  og_type: string | null;
  og_locale: string;
  twitter_card: string | null;
  twitter_title: string | null;
  twitter_description: string | null;
  og_image_url: string | null;
  twitter_image_url: string | null;
  blog: string;
};

export type FaqEntity = {
  "@type": "Question";
  name: string;
  acceptedAnswer: { "@type": "Answer"; text: string };
};

export enum VerticalCategory {
  FINANCE_AND_ACCOUNTING = "Finance & Accounting",
  EXECUTIVE_EDUCATION = "Executive Education",
  MILES_HEALTHCARE = "Miles Healthcare",
  BUSINESS = "Business",
  HEALTHCARE = "healthcare",
  MBA_AI = "mab_ai",
}

export enum FinanceAndAccountingSubcategory {
  CPA = "cpa",
  CMA = "cma",
  USP = "usp",
  CFA = "cfa",
  FRM = "frm",
  EA = "ea",
}

export enum ExecutiveEducationSubCategory {
  SoBA = "soba",
  IIML_SF = "IIML-SF",
  IIMI_BA = "IIMI-BA",
  IIMI_IB = "IIMI-IB",
  XLRI_HR = "XLRI-HR",
  XLRI_DTHRA = "XLRI-DTHRA",
  MBA_ABROAD = "mba abroad",
  MBA_ONLINE_AI = "mba online ai",
}

export enum MilesHealthcareSubCategory {
  NURSES = "NURSES",
  DOCTORS = "DOCTORS",
}

export enum HealthcareFormHeading {
  DOCTOR_PAGE_MIDDLE = "doctor page middle",
  DOCTORS_PAGE_BOTTOM = "doctors page bottom",
  DOCTORS_PAGE_TOP = "doctors page top",
  NURSES_PAGE_BOTTOM = "nurses page bottom",
  NURSES_PAGE_TOP = "nurses page top",
  MBA_AI = "ba_ai",
}

export enum OtherFormCategory {
  CONTACT_US = "Contact-us",
  QUICK_CONNECT = "quickConnect",
  BLOG_PAGE_FORM = "blog page form",
}

export enum FormHeading {
  CONNECT_WITH_A_CPA_EXPERT_FOR_DETAILS = "Connect with a CPA expert for details",
  CONNECT_WITH_A_CMA_EXPERT_FOR_DETAILS = "Connect with a CMA expert for details",
  CONNECT_WITH_A_CPA_CMA_EXPERT_FOR_DETAILS = "Connect with a CPA/CMA expert for details",
  SCHEDULE_A_VISIT_TO_MILES_OFFICE_OR_MEET_US_ONLINE = "Schedule a visit to Miles office or meet us online",
  APPLY_FOR_THE_MILES_US_PATHWAY_TO_LIVE_THE_AMERICAN_DREAM = "Apply for the Miles US Pathway to live The American Dream",
  LIMITED_SEATS_HURRY = "Limited Seats! Hurry!",
  FALL_2025_BATCH_CLOSING_SOON = "Fall 2025 Batch Closing Soon",
  RECEIVE_PROGRAM_BROCHURE_VIA_EMAIL = "Receive program brochure via email",
  JOIN_THE_WEBINAR_AND_MAKE_THE_BIG_DECISION = "Join the webinar & make the BIG decision!",
  JOIN_MILES_SOBA_TODAY = "Join Miles SOBA today!",
  JOIN_MILES_MBA_ABROAD_TODAY = "Join Miles MBA Abroad Today!",
  JOIN_MILES_GLOBAL_MBA_TODAY = "Join Miles Global MBA Today!",
  IIM_INDORE_PG_CERTIFICATION_IN_BRANDING_AND_ADVERTISING = "IIM Indore - PG Certification in Branding & Advertising",
  CONTACT_US = "Contact us",
  JOIN_US_THIS_SUNDAY_AT_4_PM_FOR_A_LIVE_WEBINAR = "Join us this Sunday at 4:00 PM for a live webinar!",
  WORLD_FAVORITE_CMA_CPA_INSTRUCTOR = "World's favorite CMA & CPA instructor",
  MBA_ONLINE_AI = "Webinar on MBA in Artificial Intelligence",
  KICKSTART_YOUR_CPA_CAREER_HERE = "Kickstart Your CPA Career here",
  KICKSTART_YOUR_CMA_CAREER_HERE = "Kickstart Your CMA Career here",
  CPA_ACCOUNTING = "Connect with us",
  JOIN_US_LIVE_WITH_VARUN_JAIN = "Join us live with Varun Jain",
  LIVE_WEBINAR_CPA_CMA = "Live Webinar: AI in Accounting 101 Webinar",
  WEEKLY_LIVE_WEBINAR_WITH_VARUN_JAIN = "Weekly Live Webinar with Varun Jain",
  TALK_TO_CAIRA_ADVISOR = "Talk to a CAIRA Advisor",
  TALK_TO_CAIRA_AUDITORS = "Talk to a CAIRA Auditor",
  GET_YOUR_CAIRA_ROADMAP = "Get your CAIRA Roadmap",
  SCHEDULE_A_VISIT_TO_MILES_OFFICE = "SCHEDULE_A_VISIT_TO_MILES_OFFICE",
  TALK_TO_AN_EXPERT = "TALK_TO_AN_EXPERT",
  LIVE_AI_IN_ACCOUNTING_101_WEBINAR = "Live AI-in-Accounting 101 Webinar",
  LIVE_AI_IN_ACCOUNTING_101_WEBINAR_POPUP = "Live AI-in-Accounting 101 Webinar Popup",
  LIVE_DATE_TIME = "Live date and time",
  MONTHLY_WEBINAR = "Monthly Webinar",
  WEEKLY_WEBINAR = "Weekly Webinar",
  AI_INTEGRATED_WEBINAR_CPA = "AI-Integrated Webinar CPA",
  AI_INTEGRATED_WEBINAR_CMA = "AI-Integrated Webinar CMA",
  AI_ACCOUNTING_101_WEBINAR = "AI_ACCOUNTING_101_WEBINAR",
  POP_UP_FORM = "Popup Form",
  QUICK_CONNECT = "Quick contact",
  ONLINE_EVENT_WEBINAR = "Online Event Webinar",
}

export enum SuccessMessageType {
  QUICK_CONNECT = "quick connect success messsage",
  CONTACT_US = "contact us success message",
  ACCOUNTING = "accounting message",
  SOBA_BUSINESS_AND_LIBERALS = "Business soba business and liberals",
  MBA_ABROAD = "mba abroad",
  IIMI_BA = "IIMA_BA",
  LIVE_WEBINAR = "live webinar",
  DOWNLOAD_BROCHURE = "download brochure",
  BLOG_PAGE_FORM = "blog page form",
  MBA_ONLINE_AI = "mba ai",
  CAIRA = "caira",
  EVENTS = "events",
}

export enum PageName {
  CPA = "cpa",
  CMA = "cma",
  MBA_AI = "mba ai",
}

export enum EventName {
  CAIRA = "caira",
}

export enum EventTypeEnum {
  ONLINE = "Online",
  OFFLINE = "Offline",
}

export type AlumniWithoutVideo = Omit<Alumni, "storyYoutubeVideoLink">;
